// One place that knows how to get credits: opens the sheet, buys the packs on the card on file
// (or starts the plan early on a trial), refreshes the wallet, and runs whatever was blocked.
import { useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { track } from '@/lib/analytics';
import { errorMessage } from '@/lib/api';
import { billingApi, keys, useMe } from '@/lib/queries';
import type { CreditShortfall } from '@/lib/api';
import { CreditsSheet } from './CreditsSheet';

export type OpenCredits = (
  needed?: number,
  retry?: () => void,
  then?: string,
  /** The whole job the shortfall belongs to (a 15 s reel: `reelPrice(15).total`), so the sheet can say a pack covers it. */
  total?: number,
  /** The balance the opener knows (a 402's own numbers and ways out): it beats the cached wallet. */
  shortfall?: { balance: number; actions?: CreditShortfall['actions'] },
) => void;

export function useCreditsSheet(): { open: OpenCredits; element: ReactNode } {
  const me = useMe();
  const qc = useQueryClient();
  const [ask, setAsk] = useState<{
    needed?: number;
    retry?: () => void;
    then?: string;
    total?: number;
    shortfall?: { balance: number; actions?: CreditShortfall['actions'] };
  } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sub = me.data?.subscription ?? null;

  const settle = async (run: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await run();
      await qc.invalidateQueries({ queryKey: keys.me });
      const retry = ask?.retry;
      setAsk(null);
      retry?.();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const element =
    ask && (me.data || ask.shortfall) ? (
      <CreditsSheet
        balance={ask.shortfall?.balance ?? me.data?.credits?.balance ?? 0}
        needed={ask.needed}
        then={ask.then}
        total={ask.total}
        actions={
          ask.shortfall?.actions ??
          (sub?.status === 'trialing' ? ['buy_pack', 'start_plan'] : ['buy_pack'])
        }
        startCharge={
          sub
            ? `$${sub.dayChargeCents / 100} today for ${sub.planName} ${sub.interval === 'year' ? 'annual' : 'monthly'}`
            : undefined
        }
        card={
          sub?.cardBrand && sub.paymentLast4
            ? `${sub.cardBrand.charAt(0).toUpperCase()}${sub.cardBrand.slice(1)} ···· ${sub.paymentLast4}`
            : null
        }
        busy={busy}
        error={error}
        onBuyPack={(packs) => void settle(() => billingApi.buyPack(packs))}
        onStartPlan={() => void settle(() => billingApi.startPlanNow())}
        onClose={() => {
          if (!busy) setAsk(null);
        }}
      />
    ) : null;

  return {
    open: (needed, retry, then, total, shortfall) => {
      track('credits_sheet_opened', {
        needed: needed ?? null,
        balance: shortfall?.balance ?? me.data?.credits?.balance ?? null,
        total: total ?? null,
        subscription_status: sub?.status ?? null,
      });
      setAsk({ needed, retry, then, total, shortfall });
    },
    element,
  };
}
